import { i as formatPrice } from "@theme/utils";
import { t as gsapWithCSS } from "@theme/gsap";
import { r as refreshScrollTrigger } from "@theme/gsap-utils";
//#region frontend/islands/purchase-options.js
var PurchaseOptions = class extends window.HTMLElement {
	connectedCallback() {
		this.sectionId = this.dataset.section;
		this.form = document.getElementById(`product-form-${this.sectionId}`);
		if (!this.form) return;
		if (!this.form.querySelector("[name=\"selling_plan\"]")) {
			const input = document.createElement("input");
			input.type = "hidden";
			input.name = "selling_plan";
			input.value = "";
			this.form.appendChild(input);
		}
		this.sellingPlanInput = this.form.querySelector("[name=\"selling_plan\"]");
		const variantInput = this.form.querySelector("input[name=\"id\"]");
		if (variantInput) {
			this._variantInput = variantInput;
			this._onVariantChange = () => this.onVariantChange(String(variantInput.value));
			variantInput.addEventListener("change", this._onVariantChange);
		}
		this.addEventListener("change", (event) => {
			const el = event.target;
			if (el.type === "radio") this.onRadioChange(el);
			if (el.tagName === "SELECT") this.onSelectChange(el);
		});
		this.syncFromActive({ animate: false });
		this.applyUrlSellingPlan();
		this.initRovingTabindex();
	}
	initRovingTabindex() {
		this.querySelectorAll("fieldset[data-variant-id]").forEach((fieldset) => {
			const navigable = () => [...fieldset.querySelectorAll("input[type=\"radio\"]:not([disabled])")];
			const syncTabindex = () => {
				const radios = navigable();
				const checked = radios.find((r) => r.checked);
				radios.forEach((r) => {
					r.setAttribute("tabindex", r === (checked ?? radios[0]) ? "0" : "-1");
				});
				fieldset.querySelectorAll("input[type=\"radio\"][disabled]").forEach((r) => r.setAttribute("tabindex", "-1"));
			};
			syncTabindex();
			fieldset.querySelectorAll("input[type=\"radio\"]").forEach((radio) => {
				radio.addEventListener("keydown", (e) => {
					const radios = navigable();
					const idx = radios.indexOf(radio);
					if (idx === -1) return;
					let target = null;
					if (e.key === "ArrowDown" || e.key === "ArrowRight") {
						e.preventDefault();
						target = radios[(idx + 1) % radios.length];
					} else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
						e.preventDefault();
						target = radios[(idx - 1 + radios.length) % radios.length];
					}
					if (target) {
						radios.forEach((r) => r.setAttribute("tabindex", "-1"));
						target.setAttribute("tabindex", "0");
						target.focus();
						target.checked = true;
						target.dispatchEvent(new Event("change", { bubbles: true }));
					}
				});
			});
			fieldset.addEventListener("change", (e) => {
				if (e.target.type === "radio") syncTabindex();
			});
		});
	}
	disconnectedCallback() {
		if (this._variantInput && this._onVariantChange) this._variantInput.removeEventListener("change", this._onVariantChange);
	}
	onVariantChange(variantId) {
		this.querySelectorAll("fieldset[data-variant-id]").forEach((fieldset) => {
			fieldset.hidden = fieldset.dataset.variantId !== variantId;
		});
		this.syncFromActive({ animate: false });
	}
	updateBuyPrice(cents) {
		const el = document.querySelector(`[data-variant-price][data-section="${this.sectionId}"]`);
		if (el && cents != null) el.textContent = formatPrice(Number(cents));
	}
	panelsIn(fieldset) {
		return [...fieldset.querySelectorAll("[data-selling-plan-details], [data-trial-details]")];
	}
	panelFor(radio) {
		return radio.closest("label")?.querySelector("[data-selling-plan-details], [data-trial-details]") ?? null;
	}
	setPanel(panel, open, { animate = true } = {}) {
		if (!panel) return;
		const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		if (!animate || prefersReduced) {
			panel.style.height = open ? "auto" : "0px";
			panel.style.overflow = open ? "visible" : "hidden";
			return;
		}
		panel.style.overflow = "hidden";
		gsapWithCSS.to(panel, {
			height: open ? panel.scrollHeight : 0,
			duration: .6,
			ease: "power3.out",
			overwrite: "auto",
			onComplete: () => {
				if (open) {
					panel.style.height = "auto";
					panel.style.overflow = "visible";
				}
				refreshScrollTrigger();
			}
		});
	}
	refuseTrialSelection(radio) {
		console.error("[purchase-options] trial radio has no data-trial-selling-plan; refusing to add a trial line as a one-time purchase");
		const error = document.querySelector("product-form [data-error-message]");
		if (error) {
			error.textContent = "We could not load your trial plan. Please refresh and try again.";
			error.hidden = false;
		}
		const subscribe = radio.closest("fieldset")?.querySelector("input[data-radio-type=\"subscribe\"]");
		if (!subscribe) return;
		radio.checked = false;
		subscribe.checked = true;
		this.applySelection(subscribe);
		this.syncPanels(subscribe, { animate: false });
	}
	applySelection(radio) {
		const fieldset = radio.closest("fieldset");
		const select = fieldset.querySelector("[data-selling-plan-select]");
		const type = radio.dataset.radioType;
		document.dispatchEvent(new CustomEvent("purchase-option:change", { detail: {
			sectionId: this.sectionId,
			subscribed: type !== "one_time"
		} }));
		if (type === "trial") {
			const plan = radio.dataset.trialSellingPlan;
			if (!plan) {
				this.refuseTrialSelection(radio);
				return;
			}
			this.sellingPlanInput.value = plan;
			this.updateBuyPrice(radio.dataset.trialPriceCents);
			return;
		}
		if (type === "subscribe") {
			this.sellingPlanInput.value = select?.value ?? "";
			this.updateBuyPrice(select?.selectedOptions[0]?.dataset.priceCents);
			return;
		}
		this.sellingPlanInput.value = "";
		this.updateBuyPrice(fieldset.dataset.variantPriceCents);
	}
	syncPanels(radio, { animate = true } = {}) {
		const fieldset = radio.closest("fieldset");
		const active = this.panelFor(radio);
		for (const panel of this.panelsIn(fieldset)) this.setPanel(panel, panel === active, { animate });
	}
	onRadioChange(radio) {
		this.applySelection(radio);
		this.syncPanels(radio, { animate: true });
	}
	onSelectChange(select) {
		this.sellingPlanInput.value = select.value;
		const fieldset = select.closest("fieldset");
		const option = select.selectedOptions[0];
		if (option?.dataset.discount) fieldset.querySelectorAll("[data-discount-percentage]").forEach((el) => {
			el.textContent = option.dataset.discount;
		});
		if (option?.dataset.discountAfter) fieldset.querySelectorAll("[data-discount-percentage-after]").forEach((el) => {
			el.textContent = option.dataset.discountAfter;
		});
		if (option?.dataset.price) {
			const el = fieldset.querySelector("[data-subscription-price]");
			if (el) el.textContent = option.dataset.price;
		}
		if (option?.dataset.perMeal) {
			const el = fieldset.querySelector("[data-per-meal]:not(option)");
			if (el) el.textContent = option.dataset.perMeal;
		}
		if (option?.dataset.priceCents) this.updateBuyPrice(option.dataset.priceCents);
	}
	applyUrlSellingPlan() {
		const planId = new URLSearchParams(window.location.search).get("sellingPlan");
		if (!planId) return;
		const fieldset = this.querySelector("fieldset[data-variant-id]:not([hidden])");
		if (!fieldset) return;
		const select = fieldset.querySelector("[data-selling-plan-select]");
		if (!select || !select.querySelector(`option[value="${planId}"]`)) return;
		select.value = planId;
		const subscribeRadio = fieldset.querySelector("input[data-radio-type=\"subscribe\"]");
		if (subscribeRadio) subscribeRadio.checked = true;
		this.onSelectChange(select);
		this.syncFromActive({ animate: false });
	}
	syncFromActive({ animate = true } = {}) {
		const fieldset = this.querySelector("fieldset[data-variant-id]:not([hidden])");
		if (!fieldset) return;
		const checkedRadio = fieldset.querySelector("input[type=\"radio\"]:checked");
		if (!checkedRadio) return;
		this.applySelection(checkedRadio);
		this.syncPanels(checkedRadio, { animate });
	}
};
window.customElements.define("purchase-options", PurchaseOptions);
//#endregion
